import { OauthAttemptStatus } from "@coeff/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { QueryCacheKey } from "../api/queryClient";
import { config } from "../config";
import { sentryCapture, useApiContext } from "../utils";

declare const __LOCAL_DEVELOPMENT_NGROK_API_URL__: string;

// Must stay a superset of the scopes the backend requests at token exchange /
// refresh (see oauth2_proxy_controller._get_oauth_params_for_service and
// ms_teams_utils.refresh_ms_teams_token). The authorize step grants the scopes;
// the token exchange then requests a subset. If a backend scope is missing here
// the authorize consent does not cover it and the token exchange fails with
// AADSTS65001 ("consent not granted"). Keep these two lists in sync.
const MS_TEAMS_OAUTH_SCOPES =
  "offline_access Team.ReadBasic.All Channel.ReadBasic.All TeamMember.Read.All " +
  "User.Read User.ReadBasic.All TeamsAppInstallation.ReadForUser";

const buildMSTeamsAuthUrl = (oauthAttemptId: string, email: string): string => {
  const redirectUri = `${config.OAUTH_REDIRECT_URL_BASE}/ms_teams`;
  const tenantId =
    process.env.NODE_ENV === "production" ? "common" : config.MS_TEAMS_BOT_TENANT_ID || "common";

  const state = JSON.stringify({
    from: "webapp",
    oauth_attempt_id: oauthAttemptId,
    email,
    data_source_type: "ms_teams",
    ...(__LOCAL_DEVELOPMENT_NGROK_API_URL__
      ? {
          coeff_dev_redirect_base_url: __LOCAL_DEVELOPMENT_NGROK_API_URL__,
        }
      : {}),
  });

  const params = new URLSearchParams({
    client_id: config.MS_TEAMS_BOT_APP_ID || config.MS_IDP_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: MS_TEAMS_OAUTH_SCOPES,
    response_type: "code",
    response_mode: "query",
    // "select_account" shows the account picker without forcing the consent
    // screen on every attempt. "consent" forced re-consent each time, which —
    // combined with admin-consent-required scopes — left users stuck on the
    // "approval required" prompt even after a tenant admin had granted consent.
    prompt: "select_account",
    state,
  });

  return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
};

type UseMSTeamsOAuthOptions = {
  userEmail: string;
  onSuccess?: () => void;
  onFailure?: (errorMessage: string) => void;
};

type UseMSTeamsOAuthReturn = {
  isAuthorized: boolean | undefined;
  isCheckingStatus: boolean;
  isAuthInProgress: boolean;
  startOAuth: () => Promise<void>;
  cancelOAuth: () => void;
  refetchAuthStatus: () => void;
};

export const useMSTeamsOAuth = (options: UseMSTeamsOAuthOptions): UseMSTeamsOAuthReturn => {
  const { userEmail, onSuccess, onFailure } = options;
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  const [oauthAttemptId, setOauthAttemptId] = useState<string | null>(null);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const oauthPopupRef = useRef<Window | null>(null);

  const {
    data: authStatusData,
    isLoading: isCheckingStatus,
    refetch: refetchAuthStatus,
  } = useQuery(
    [QueryCacheKey.MS_TEAMS_AUTH_STATUS],
    async () => {
      const response = await apiClient.getMSTeamsAuthStatus();
      return response.data;
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const { data: oauthAttemptData } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT, oauthAttemptId],
    async () => {
      if (!oauthAttemptId) {
        return null;
      }
      const response = await apiClient.getOauthAttempt({ oauth_attempt_id: oauthAttemptId });
      return response.data;
    },
    {
      enabled: Boolean(isAuthInProgress && oauthAttemptId),
      refetchInterval: data =>
        data?.oauth_attempt?.status === OauthAttemptStatus.InProgress ? 2000 : false,
    }
  );

  useEffect(() => {
    if (!oauthAttemptData?.oauth_attempt) {
      return;
    }

    const { status, error_message } = oauthAttemptData.oauth_attempt;

    if (status === OauthAttemptStatus.Success) {
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
      oauthPopupRef.current = null;
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      // Invalidate auth status and channels list so reconnect/reauthorize refreshes teams/channels.
      queryClient.invalidateQueries([QueryCacheKey.MS_TEAMS_AUTH_STATUS]);
      queryClient.invalidateQueries([QueryCacheKey.MS_TEAMS_TEAMS_AND_CHANNELS]);
      onSuccess?.();
    } else if (status === OauthAttemptStatus.Failure) {
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      onFailure?.(error_message ?? "Microsoft Teams authorization failed");
    }
  }, [oauthAttemptData, onSuccess, onFailure, queryClient]);

  // New tab must open synchronously on user click; opening only after await is commonly blocked.
  const startOAuth = useCallback(async () => {
    let tab: Window | null = null;
    try {
      setIsAuthInProgress(true);

      tab = window.open("about:blank", "_blank");
      if (!tab || tab.closed) {
        setIsAuthInProgress(false);
        setOauthAttemptId(null);
        onFailure?.(
          "Could not open a new tab. Allow pop-ups for this site or try again from the address bar."
        );
        return;
      }
      oauthPopupRef.current = tab;

      const { data } = await apiClient.createOauthAttempt({
        create_oauth_attempt_request: {
          data_source_type: "ms_teams",
        },
      });

      const attemptId = data.oauth_attempt.oauth_attempt_id;
      setOauthAttemptId(attemptId);

      const authUrl = buildMSTeamsAuthUrl(attemptId, userEmail);
      tab.location.href = authUrl;
    } catch (error) {
      if (tab && !tab.closed) {
        tab.close();
      }
      oauthPopupRef.current = null;
      sentryCapture({ error: error as Error });
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      onFailure?.("Failed to start Microsoft Teams authorization. Please try again.");
    }
  }, [apiClient, userEmail, onFailure]);

  const cancelOAuth = useCallback(() => {
    if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
      oauthPopupRef.current.close();
    }
    oauthPopupRef.current = null;
    setIsAuthInProgress(false);
    setOauthAttemptId(null);
  }, []);

  return {
    isAuthorized: authStatusData?.has_ms_teams_auth,
    isCheckingStatus,
    isAuthInProgress,
    startOAuth,
    cancelOAuth,
    refetchAuthStatus,
  };
};
